/**
 * Raised-region footprint via room-polygon clipping.
 *
 * The raised floor is the portion of the ROOM floor polygon on the raised
 * side of the step line — not a convex hull of the furniture (which cuts a
 * diagonal between the outermost pieces and ignores the walls). We find the
 * step line from the low→raised cluster direction (cardinal-snapped, since
 * aligned rooms are axis-aligned), anchor its position on the stairs, and
 * clip the room polygon by that half-plane. The edges the clip introduces
 * are the exposed riser(s).
 *
 * Pure geometry. X-Z plane.
 */

const { convexHull } = require('../geometry/obb');

const ON_LINE_EPS = 1e-4;
// How far the stairs-top anchor may be from a room wall for the step line to
// snap onto that wall (the real divider between the two levels).
const WALL_SNAP_TOL_M = 0.6;

/** Nearest cardinal unit vector (±x or ±z) to a 2D vector. */
function cardinalSnap(vec) {
  return Math.abs(vec.x) >= Math.abs(vec.z)
    ? { x: Math.sign(vec.x) || 1, z: 0 }
    : { x: 0, z: Math.sign(vec.z) || 1 };
}

function dot(p, u) {
  return p.x * u.x + p.z * u.z;
}

function polygonArea(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.z - q.x * p.z;
  }
  return Math.abs(a) / 2;
}

/**
 * Clip a polygon to the half-plane { p : dot(p,u) >= linePos } via
 * Sutherland-Hodgman. Returns the clipped polygon (possibly empty).
 */
function clipPolygonByHalfPlane(poly, u, linePos) {
  const out = [];
  const n = poly.length;
  // Snap a near-zero distance to exactly zero FIRST, so a vertex on the clip
  // line is unambiguously in and the crossing test below only fires on a real
  // sign change. Testing `>= -EPS` while interpolating `dA / (dA - dB)` mixes
  // two different notions of "on the line": when both distances sit inside the
  // epsilon band but on the same side, that ratio leaves [0, 1] and
  // extrapolates a vertex off the end of the edge. Clipping a landing footprint
  // to the stair flights hits this whenever the flights reach the room wall —
  // on Curlew two corners 4.4e-5 m apart straddled the band and produced
  // t = -1.62, a self-intersecting slab.
  const signed = (p) => {
    const d = dot(p, u) - linePos;
    return Math.abs(d) <= ON_LINE_EPS ? 0 : d;
  };
  for (let i = 0; i < n; i++) {
    const A = poly[i];
    const B = poly[(i + 1) % n];
    const dA = signed(A);
    const dB = signed(B);
    const inA = dA >= 0;
    const inB = dB >= 0;
    if (inA) out.push(A);
    if (inA !== inB) {
      const t = dA / (dA - dB);
      if (t > 0 && t < 1) {
        out.push({ x: A.x + t * (B.x - A.x), z: A.z + t * (B.z - A.z) });
      }
    }
  }
  return out;
}

/**
 * Edges of a clipped polygon that lie ON the clip line — i.e. the exposed
 * riser segments the clip introduced. Returns array of { a, b }.
 */
function edgesOnLine(poly, u, linePos) {
  const edges = [];
  const onLine = (p) => Math.abs(dot(p, u) - linePos) <= ON_LINE_EPS;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    if (
      onLine(a) &&
      onLine(b) &&
      Math.hypot(b.x - a.x, b.z - a.z) > ON_LINE_EPS
    ) {
      edges.push({ a, b });
    }
  }
  return edges;
}

function centroidOf(points) {
  const n = points.length || 1;
  return {
    x: points.reduce((s, p) => s + p.x, 0) / n,
    z: points.reduce((s, p) => s + p.z, 0) / n,
  };
}

/**
 * Snap the step position to the wall(s) that divide the two levels. The
 * divider is usually not one clean wall but a staircase of segments (two
 * faces of a thick partition, jogs, a doorway gap), so we take the
 * length-weighted centerline of EVERY room wall that runs perpendicular to
 * the step axis `u` and sits within WALL_SNAP_TOL_M of the `candidate`
 * position — a single fragile "nearest wall" snap collapses onto one face.
 * Returns the cluster centerline along u, or null if no divider is near.
 */
function snapToDividerWalls(walls, u, candidate) {
  let weightedSum = 0;
  let totalLen = 0;
  for (const w of walls || []) {
    const pc = w.polygonCorners;
    if (!pc || pc.length < 2) continue;
    const us = pc.map((c) => dot(c, u));
    const ps = pc.map((c) => dot(c, { x: -u.z, z: u.x }));
    const uSpan = Math.max(...us) - Math.min(...us);
    const pSpan = Math.max(...ps) - Math.min(...ps);
    if (uSpan >= pSpan) continue; // not perpendicular to u
    const uMid = (Math.min(...us) + Math.max(...us)) / 2;
    if (Math.abs(uMid - candidate) > WALL_SNAP_TOL_M) continue;
    weightedSum += uMid * pSpan;
    totalLen += pSpan;
  }
  return totalLen > 0 ? weightedSum / totalLen : null;
}

/**
 * Compute the raised footprint + riser edges by clipping the room polygon.
 *
 * @param {Object} args
 * @param {Array<{x,z}>} args.roomPolygon - base room floor polygon (x-z)
 * @param {{x,z}} args.raisedCentroid - centroid of raised floor-standing objs
 * @param {{x,z}|null} args.baseCentroid - centroid of base floor-standing objs
 * @param {Array<{x,z}>|null} args.stairsFootprint - corroborating stairs
 *   footprint corners; the step line is anchored on its UPHILL edge (the top
 *   of the stairs), where the raised floor actually begins.
 * @param {Array<{x,z}>} [args.memberFootprintPoints] - fallback hull points
 * @param {{x,z}} [args.uphill] - the low->raised direction, already resolved.
 *   Supplied by the stairs-only detector (`detectStairsStep`), which has no
 *   furniture cluster to point uphill and settles the direction from the two
 *   sides' wall heights instead. When absent the direction is derived from the
 *   centroids below, exactly as before.
 * @returns {{ footprint: Array<{x,z}>, riserEdges: Array<{a,b}>, method: string }}
 */
function computeRaisedFootprint({
  roomPolygon,
  roomWalls,
  raisedCentroid,
  baseCentroid,
  stairsFootprint,
  memberFootprintPoints = [],
  uphill = null,
}) {
  const stairsCenter =
    stairsFootprint && stairsFootprint.length
      ? centroidOf(stairsFootprint)
      : null;
  const hullFallback = () => ({
    footprint: convexHull(memberFootprintPoints),
    riserEdges: [],
    method: 'hull-fallback',
  });

  if (!roomPolygon || roomPolygon.length < 3) return hullFallback();

  // Uphill direction (low → raised), cardinal-snapped. An explicit `uphill`
  // wins: the caller that supplies one resolved it from evidence this function
  // cannot see. Otherwise prefer the stairs→raised vector (robust when few base
  // objects were captured) and fall back to base→raised.
  let u;
  if (uphill) {
    u = cardinalSnap(uphill);
  } else {
    const from = stairsCenter || baseCentroid;
    if (!from) return hullFallback();
    u = cardinalSnap({
      x: raisedCentroid.x - from.x,
      z: raisedCentroid.z - from.z,
    });
  }

  // Step-line position along u: first an anchor at the stairs' UPHILL edge
  // (its top, where the raised floor begins), else the midpoint between the
  // base and raised clusters. Then snap to the real dividing wall when one
  // sits within tolerance — the wall, not the stairs, is the true boundary.
  let candidate;
  if (stairsFootprint && stairsFootprint.length) {
    candidate = Math.max(...stairsFootprint.map((p) => dot(p, u)));
  } else if (baseCentroid) {
    candidate = (dot(baseCentroid, u) + dot(raisedCentroid, u)) / 2;
  } else {
    return hullFallback();
  }
  const dividerPos = snapToDividerWalls(roomWalls, u, candidate);
  const linePos = dividerPos != null ? dividerPos : candidate;

  const footprint = clipPolygonByHalfPlane(roomPolygon, u, linePos);
  if (footprint.length < 3 || polygonArea(footprint) < 0.5) {
    return hullFallback();
  }

  return {
    footprint,
    riserEdges: edgesOnLine(footprint, u, linePos),
    method: 'polygon-clip',
    axis: u,
    linePos,
  };
}

module.exports = {
  computeRaisedFootprint,
  clipPolygonByHalfPlane,
  edgesOnLine,
  cardinalSnap,
  centroidOf,
  polygonArea,
};
